import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getTheme } from '../theme';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => getTheme(props.theme?.isDark).colors.background};
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
  position: relative;
  overflow: hidden;

  ${props => props.theme?.isDark && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: 
        repeating-linear-gradient(
          0deg,
          rgba(25, 184, 136, 0.03) 0px,
          transparent 1px,
          transparent 2px,
          rgba(25, 184, 136, 0.03) 3px
        );
      pointer-events: none;
      z-index: 1;
    }
  `}
`;

const LoginCard = styled.div`
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.xl};
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  width: 100%;
  max-width: 400px;
  box-shadow: ${props => getTheme(props.theme?.isDark).shadows.xl};
  position: relative;
  z-index: 2;

  ${props => props.theme?.isDark && `
    box-shadow: 0 0 30px rgba(25, 184, 136, 0.2);
    border-color: ${getTheme(true).colors.border};
  `}
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xl};
`;

const Logo = styled.img`
  height: 60px;
  width: auto;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.primary};
  margin: 0;
  ${props => props.theme?.isDark && 'text-shadow: 0 0 10px rgba(25, 184, 136, 0.5);'}
`;

const Subtitle = styled.p`
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.sm};
  font-size: 14px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
`;

const Input = styled.input`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  transition: all 0.2s ease;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};

  ${props => props.theme?.isDark && `
    background-color: ${getTheme(true).colors.surface};
    border-color: ${getTheme(true).colors.border};
    color: ${getTheme(true).colors.text};
    
    &:hover {
      border-color: ${getTheme(true).colors.primary};
      box-shadow: 0 0 5px rgba(25, 184, 136, 0.2);
    }
  `}

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark ? `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3), 0 0 0 2px rgba(25, 184, 136, 0.1);
      background-color: ${getTheme(true).colors.surface};
    ` : `
      box-shadow: 0 0 0 2px ${getTheme(false).colors.primaryLight};
    `}
  }

  &::placeholder {
    color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
    opacity: ${props => props.theme?.isDark ? 0.6 : 1};
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    ${props => props.theme?.isDark ? `
      -webkit-text-fill-color: ${getTheme(true).colors.text};
      -webkit-box-shadow: 0 0 0px 1000px ${getTheme(true).colors.surface} inset;
      transition: background-color 5000s ease-in-out 0s;
    ` : ''}
  }
`;

const Button = styled.button`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  color: white;
  border: none;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};

  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.primaryDark};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 20px rgba(25, 184, 136, 0.5);
    `}
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.error}20;
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.error};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  color: ${props => getTheme(props.theme?.isDark).colors.error};
  font-size: 14px;
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const theme = { isDark };
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (!isMountedRef.current) return;
    
    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.error);
    }
    
    if (isMountedRef.current) {
      setLoading(false);
    }
  };

  return (
    <LoginContainer theme={theme}>
      <LoginCard theme={theme}>
        <LogoContainer>
          <Logo src="/fit-logo.png" alt="Fit Logo" />
          <Title theme={theme}>Admin Panel</Title>
          <Subtitle theme={theme}>Sign in to continue</Subtitle>
        </LogoContainer>
        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}
          <InputGroup>
            <Label theme={theme}>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              theme={theme}
            />
          </InputGroup>
          <InputGroup>
            <Label theme={theme}>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              theme={theme}
            />
          </InputGroup>
          <Button type="submit" disabled={loading} theme={theme}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;

